import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, ChefHat, CalendarDays, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Relative path: served by the FastAPI backend (/api prefix -> ingress) today,
// and by the Cloudflare Pages Function at functions/api/menu-del-giorno.js
// after the Cloudflare migration — same origin in both cases.
const MENU_ENDPOINT = '/api/menu-del-giorno';

/**
 * Menu Attuale — fed by a public Google Sheet (Foglio1) via a serverless
 * endpoint (`/api/menu-del-giorno`). The sheet structure is:
 *   data | categoria | nome_piatto | prezzo | attivo | ordine
 * The endpoint filters inactive rows, sorts by `ordine` and groups by category.
 * If the fetch fails or the sheet is empty we fall back to an elegant
 * "coming soon" placeholder so the page never looks broken.
 */
const MenuAttuale = () => {
  const { t } = useLanguage();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (forceRefresh) => {
    try {
      const url = `${MENU_ENDPOINT}${forceRefresh ? '?refresh=true' : ''}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMenu(data);
    } catch (err) {
      // Swallow the error on purpose: a friendly "coming soon" message is
      // shown whenever there are no items, regardless of the reason.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load(false);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    await load(true);
    setRefreshing(false);
  };

  const hasItems = menu?.categories?.some((c) => c.items?.length > 0);
  const validityDate = menu?.categories?.[0]?.items?.[0]?.date || null;
  const lastUpdatedLabel = menu?.updated_at
    ? new Date(menu.updated_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <section
      id="menu-attuale"
      className="py-24 bg-gradient-to-b from-stone-900 to-stone-950 relative overflow-hidden"
    >
      {/* Decorative top/bottom borders */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
      <div className="absolute top-20 right-10 w-72 h-72 bg-amber-600/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-amber-700/5 rounded-full blur-3xl" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-600/20 border border-amber-600/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">{t('menuAttuale.badge')}</span>
          </div>
          <p className="text-amber-500 uppercase tracking-[0.3em] text-sm mb-4">
            {t('menuAttuale.subtitle')}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white">
            {t('menuAttuale.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-6" />

          {validityDate && hasItems && (
            <div
              data-testid="menu-attuale-validity"
              className="mt-6 inline-flex items-center gap-2 text-amber-200/80 text-sm"
            >
              <CalendarDays className="w-4 h-4" />
              <span>
                {t('menuAttuale.validFor')} <span className="text-amber-300 font-medium">{validityDate}</span>
              </span>
            </div>
          )}

          <div className="mt-6 flex flex-col items-center gap-2">
            <button
              type="button"
              data-testid="menu-attuale-refresh-button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 rounded-full border border-amber-600/40 bg-amber-600/10 px-4 py-2 text-xs font-medium uppercase tracking-wider text-amber-300 transition-all duration-300 hover:bg-amber-600/20 hover:border-amber-500/60 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? t('menuAttuale.refreshing') : t('menuAttuale.refresh')}
            </button>
            {lastUpdatedLabel && (
              <span data-testid="menu-attuale-last-updated" className="text-amber-200/50 text-xs">
                {t('menuAttuale.lastUpdated')}: {lastUpdatedLabel}
              </span>
            )}
          </div>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div data-testid="menu-attuale-loading" className="max-w-3xl mx-auto space-y-6">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-28 rounded-2xl bg-stone-900/60 border border-amber-700/20 animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Live menu */}
        {!loading && hasItems && (
          <div
            data-testid="menu-attuale-list"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          >
            {menu.categories.map((category, idx) => (
              <article
                key={`${category.name}-${idx}`}
                className="group relative bg-gradient-to-br from-stone-900/80 via-stone-900/60 to-amber-950/30 border border-amber-700/30 rounded-3xl p-7 md:p-8 transition-all duration-500 hover:border-amber-500/60 hover:shadow-2xl hover:shadow-amber-900/30 hover:-translate-y-1"
              >
                <div className="pointer-events-none absolute -top-16 right-0 w-40 h-40 bg-amber-500/5 rounded-full blur-3xl group-hover:bg-amber-500/10 transition" />

                <header className="flex items-baseline justify-between gap-3 mb-5 pb-4 border-b border-amber-700/20">
                  <h3 className="font-serif text-xl md:text-2xl font-semibold text-white tracking-wide">
                    {category.name}
                  </h3>
                  <span className="text-amber-500/60 text-xs uppercase tracking-[0.25em]">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                </header>

                <ul className="space-y-5">
                  {category.items.map((item, j) => {
                    const lines = item.name.split('\n');
                    return (
                      <li
                        key={j}
                        data-testid={`menu-attuale-item-${idx}-${j}`}
                        className="flex items-start justify-between gap-4"
                      >
                        <div className="flex-1 min-w-0">
                          {lines.map((line, k) => (
                            <p
                              key={k}
                              className={
                                k === 0
                                  ? 'text-amber-100 leading-snug'
                                  : 'text-amber-100/70 text-sm leading-snug'
                              }
                            >
                              {line}
                            </p>
                          ))}
                        </div>
                        {item.price && (
                          <span className="shrink-0 font-serif text-amber-300 text-base md:text-lg font-medium tabular-nums">
                            {item.price}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </article>
            ))}
          </div>
        )}

        {/* Empty / error fallback — keeps the elegant Coming Soon */}
        {!loading && !hasItems && (
          <div
            data-testid="menu-attuale-coming-soon"
            className="relative max-w-2xl mx-auto"
          >
            <div className="relative bg-gradient-to-br from-stone-900/80 via-stone-900/60 to-amber-950/30 border border-amber-700/30 rounded-3xl p-10 md:p-14 text-center overflow-hidden">
              <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl" />

              <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 mb-6 shadow-xl shadow-amber-900/40 ring-4 ring-amber-600/10 animate-pulse">
                <ChefHat className="w-10 h-10 text-white" />
              </div>

              <h3 className="font-serif text-2xl md:text-3xl font-semibold text-white mb-4">
                {t('menuAttuale.comingSoonTitle')}
              </h3>

              <div className="flex items-center justify-center gap-2 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500/60 animate-pulse" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500/60 animate-pulse" style={{ animationDelay: '200ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500/60 animate-pulse" style={{ animationDelay: '400ms' }} />
              </div>

              <p className="text-amber-100/70 text-base md:text-lg leading-relaxed max-w-md mx-auto">
                {t('menuAttuale.comingSoonText')}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default MenuAttuale;
